/**
 * Contractor Availability — declarative operational capacity layer.
 *
 * Eligible ≠ available. A contractor can be approved, qualified, and in
 * territory while still being unavailable, fully booked, on PTO, or
 * temporarily paused. This module is the canonical resolver + writer for
 * that declarative capacity signal.
 *
 * Surfaces consume `getContractorAvailability()` to bucket recommendations
 * (Available / Limited / At Capacity / PTO / Paused) — NEVER to filter
 * them out. The dispatcher always retains the ability to "offer anyway"
 * against a non-available contractor with a typed reason. This matches
 * the LOCKED operating principle:
 *
 *   availability is declarative operational capacity, layered on top of
 *   eligibility — it never silently removes installers from view.
 *
 * Hard rules:
 *   - Resolver is pure (contractor + jobs + today → AvailabilityState).
 *   - "at_capacity" is DERIVED from active jobs vs weeklyJobCap; never
 *     persisted on the contractor record.
 *   - Default fallback when nothing is declared = `{ mode: "available" }`.
 *   - Writers: `setContractorAvailability` (operator-side via
 *     ContractorProfile) and `setOwnAvailability` (installer portal,
 *     constrained subset: limited / paused / pto / available).
 *
 * Anchors: Phase A of the Availability Orchestration Layer plan.
 */

import type { Contractor, Job } from "@/data/types";

export type AvailabilityMode =
  | "available"
  | "limited"
  | "paused"
  | "pto"
  | "at_capacity";

export type AvailabilityState =
  | { mode: "available" }
  | { mode: "limited"; weeklyJobCap: number; reason?: string }
  | { mode: "paused"; reason: string; until?: string /* ISO date */ }
  | { mode: "pto"; from: string; until: string }
  | { mode: "at_capacity"; activeJobs: number; weeklyJobCap: number };

/**
 * Read-only audit envelope returned alongside the resolved state so UI
 * surfaces can show "declared by / last updated".
 */
export interface ContractorAvailability {
  state: AvailabilityState;
  /** True when `at_capacity` was derived rather than declared. */
  isDerived: boolean;
  /** Source — "declared" (contractor.availability) or "derived". */
  source: "declared" | "derived" | "default";
  /** Last declarative update, if any. */
  declaredAt?: string;
  declaredBy?: string;
  /** Eligible-installer count of active jobs feeding the capacity check. */
  activeJobCount: number;
}

const DEFAULT_STATE: AvailabilityState = { mode: "available" };

/**
 * Returns the declarative state if set, otherwise derives `at_capacity`
 * from current active assignments vs. `weeklyJobCap` (when defined).
 *
 * `today` is injected for deterministic testing.
 */
export function getContractorAvailability(
  contractor: Contractor,
  jobs: Job[] = [],
  today: Date = new Date(),
): ContractorAvailability {
  const activeJobCount = jobs.filter(
    (j) =>
      j.assignedSubcontractor === contractor.companyName &&
      j.currentStage !== "Completed" &&
      j.currentStage !== "Issue",
  ).length;

  const declared = contractor.availability;

  // PTO window auto-resolves once `until` has passed.
  if (declared?.mode === "pto") {
    const until = new Date(declared.until);
    if (Number.isFinite(until.getTime()) && until.getTime() >= today.getTime()) {
      return {
        state: declared,
        isDerived: false,
        source: "declared",
        declaredAt: contractor.availabilityDeclaredAt,
        declaredBy: contractor.availabilityDeclaredBy,
        activeJobCount,
      };
    }
    // Expired PTO → fall through to capacity / default
  }

  if (declared?.mode === "paused") {
    return {
      state: declared,
      isDerived: false,
      source: "declared",
      declaredAt: contractor.availabilityDeclaredAt,
      declaredBy: contractor.availabilityDeclaredBy,
      activeJobCount,
    };
  }

  if (declared?.mode === "limited") {
    // Check if limited cap is exceeded → derive at_capacity
    if (
      declared.weeklyJobCap > 0 &&
      activeJobCount >= declared.weeklyJobCap
    ) {
      return {
        state: {
          mode: "at_capacity",
          activeJobs: activeJobCount,
          weeklyJobCap: declared.weeklyJobCap,
        },
        isDerived: true,
        source: "derived",
        declaredAt: contractor.availabilityDeclaredAt,
        declaredBy: contractor.availabilityDeclaredBy,
        activeJobCount,
      };
    }
    return {
      state: declared,
      isDerived: false,
      source: "declared",
      declaredAt: contractor.availabilityDeclaredAt,
      declaredBy: contractor.availabilityDeclaredBy,
      activeJobCount,
    };
  }

  // No declarative state — check legacy maxWeeklyJobs as soft cap.
  const cap = contractor.maxWeeklyJobs ?? 0;
  if (cap > 0 && activeJobCount >= cap) {
    return {
      state: { mode: "at_capacity", activeJobs: activeJobCount, weeklyJobCap: cap },
      isDerived: true,
      source: "derived",
      activeJobCount,
    };
  }

  return {
    state: DEFAULT_STATE,
    isDerived: false,
    source: "default",
    activeJobCount,
  };
}

/* ── Bucket helpers (consumed by Smart Dispatch / Offer Review) ─────── */

export type AvailabilityBucket = "available" | "limited" | "off";

export function bucketFor(state: AvailabilityState): AvailabilityBucket {
  switch (state.mode) {
    case "available":
      return "available";
    case "limited":
    case "at_capacity":
      return "limited";
    case "paused":
    case "pto":
      return "off";
  }
}

export const AVAILABILITY_BUCKET_LABEL: Record<AvailabilityBucket, string> = {
  available: "Available",
  limited: "Limited / At Capacity",
  off: "PTO / Paused",
};

export function describeAvailability(state: AvailabilityState): string {
  switch (state.mode) {
    case "available":
      return "Available";
    case "limited":
      return state.reason
        ? `Limited · ${state.weeklyJobCap}/wk · ${state.reason}`
        : `Limited · ${state.weeklyJobCap}/wk`;
    case "paused":
      return state.until
        ? `Paused until ${state.until} — ${state.reason}`
        : `Paused — ${state.reason}`;
    case "pto":
      return `On PTO ${state.from} → ${state.until}`;
    case "at_capacity":
      return `At capacity (${state.activeJobs}/${state.weeklyJobCap} jobs this week)`;
  }
}

/* ── Writers ──────────────────────────────────────────────────────────
 *
 * These functions return a NEW contractor list — pass them straight to
 * `setContractors(prev => setContractorAvailability(prev, ...))`. This
 * keeps the writer pure and testable while integrating with the
 * existing AppDataContext state model.
 */

export function setContractorAvailability(
  contractors: Contractor[],
  contractorId: string,
  state: AvailabilityState,
  actor: { id: string; role: string },
): Contractor[] {
  // Strip derived state from writes — only declarative modes are stored.
  if (state.mode === "at_capacity") {
    throw new Error(
      "[availability] `at_capacity` is derived, not declarative — write `limited` instead.",
    );
  }
  return contractors.map((c) =>
    c.id === contractorId
      ? {
          ...c,
          availability: state,
          availabilityDeclaredAt: new Date().toISOString(),
          availabilityDeclaredBy: `${actor.role}:${actor.id}`,
        }
      : c,
  );
}

/**
 * Constrained writer for installer self-declaration via portal. Same
 * underlying mutation but caller-side enforced to declarative modes
 * (no admin-only fields). Identical signature for now; reserved for
 * future portal-specific validation (e.g., max PTO length).
 */
export const setOwnAvailability = setContractorAvailability;
